'use client'
import React, { useImperativeHandle, useRef } from 'react'
import { cn } from '../../helpers/utils.js'
import Picture from '../../components/picture.js'
import { withLayout } from '../../shared/Styles.js'
import Title from '../Title/index.js'
import SwiperBox from './SwiperCategory.js'
import { useExposure } from '../../hooks/useExposure.js'
import { trackUrlRef } from '../../shared/trackUrlRef.js'
import type { Img } from '../../types/props.js'

const componentType = 'image'
const componentName = 'category_banner'
const MOBILE_FULL_PAGE_CAPACITY = 5
const MOBILE_COMPACT_PAGE_SIZE = 3

export interface CategoryItem {
  /** 产品图片*/
  image: Img
  name: string
  link?: string
}

export interface CategoryProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 主题色*/
  data: {
    isShowSelect: boolean
    products: CategoryItem[]
    productData: CategoryItem[]
    /** 卡片形状 */
    shape?: 'round' | 'square'
    title?: string
    theme?: 'light' | 'dark'
  }
  buildData?: {
    categories: any[]
    products: any[]
  }
  /** 按钮事件*/
  event?: {
    primaryButton?: (_v: any, _index: number) => void
  }
  key?: string
}

const chunkArray = (arr: any[], size: number) => {
  const chunks = []
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size))
  }
  return chunks
}

const CategoryPcItem = ({ data, configuration }: { data: CategoryItem; configuration?: any }) => {
  const ref = useRef<HTMLDivElement>(null)

  useExposure(ref, {
    componentType,
    componentName,
    componentTitle: data?.name,
    position: configuration?.index + 1,
  })

  return (
    <div className={cn('relative max-h-[240px] overflow-hidden')}>
      <div
        className={cn(
          'aspect-w-[167] aspect-h-[120]',
          'tablet:aspect-w-[404] tablet:aspect-h-[240]',
          'laptop:aspect-w-[212] laptop:aspect-h-[144]',
          'desktop:aspect-w-[205] desktop:aspect-h-[192]',
          'lg-desktop:aspect-w-[264] lg-desktop:aspect-h-[240]'
        )}
      >
        <div
          ref={ref}
          className={cn(
            'tablet:p-3 laptop:p-4 desktop:p-6 absolute inset-0 max-h-[240px] justify-between',
            'bg-container-primary hover:bg-info-white group box-border flex cursor-pointer flex-col overflow-hidden duration-300',
            configuration?.shape === 'round' ? 'rounded-2xl' : 'rounded-none'
          )}
        >
          <div className="flex items-center justify-center transition-all duration-300 group-hover:scale-110">
            <a
              aria-label={data?.name}
              href={trackUrlRef(data?.link, `${componentType}_${componentName}`)}
              data-headless-type-name={`${componentType}#${componentName}`}
              data-headless-title-desc-button={`${data?.name}`}
              onClick={() => configuration?.event?.primaryButton(data, configuration?.index)}
            >
              <Picture
                alt={data?.image?.alt || ''}
                source={data?.image?.url || ''}
                className="laptop:size-1/2 tablet:size-[72px] desktop:size-[116px] lg-desktop:size-[146px] m-auto flex justify-center object-cover [&_img]:h-full [&_img]:w-auto [&_img]:object-cover"
              />
            </a>
          </div>
          <p className="lg-desktop:text-lg lg-desktop:!leading-6 text-info-primary box-border line-clamp-2 text-center text-sm font-bold leading-4">
            {data?.name}
          </p>
        </div>
      </div>
    </div>
  )
}

// 移动端品类展示组件
const SingleMobileItem = ({
  data,
  configuration,
  index,
  isFirstItemLarger,
}: {
  data: CategoryItem
  configuration?: any
  index: number
  isFirstItemLarger: boolean
}) => {
  const ref = useRef<HTMLDivElement>(null)

  useExposure(ref, {
    componentType,
    componentName,
    componentTitle: data?.name,
    position: configuration?.index + 1,
  })

  const isFirstItem = index === 0

  return (
    <div
      ref={ref}
      className={cn(
        'bg-container-primary box-border flex w-full shrink-0 flex-col items-center justify-center overflow-hidden',
        'hover:bg-info-white cursor-pointer duration-300',
        // 当第一个项目需要更大空间时调整样式
        isFirstItemLarger
          ? cn('p-4', isFirstItem ? 'aspect-h-[252] row-span-2' : 'aspect-h-[120]')
          : 'max-h-[126px] px-3 py-4',
        configuration?.shape === 'round' ? 'rounded-2xl' : 'rounded-none'
      )}
    >
      <a
        href={trackUrlRef(data?.link, `${componentType}_${componentName}`)}
        data-headless-type-name={`${componentType}#${componentName}`}
        data-headless-title-desc-button={`${data?.name}`}
        data-headless-nav-position={`''#${configuration?.index + 1}`}
        onClick={() => configuration?.event?.primaryButton(data, configuration?.index + 1)}
      >
        <Picture
          source={data?.image?.url || ''}
          alt={data?.image?.alt || ''}
          className={cn(
            'm-auto mb-2 flex justify-center object-contain [&_img]:h-full [&_img]:w-auto [&_img]:object-contain',
            // 当第一个项目需要更大空间时调整图片尺寸
            isFirstItemLarger
              ? cn(isFirstItem ? 'size-full max-h-[146px] overflow-hidden' : 'max-h-[72px] max-w-[72px]')
              : 'size-[72px]'
          )}
        />
      </a>
      <p
        className={cn(
          'text-info-primary w-full text-center text-sm font-bold',
          // 当第一个项目需要更大空间时调整文字显示
          isFirstItemLarger ? 'line-clamp-2' : 'shrink-0 truncate'
        )}
      >
        {data?.name}
      </p>
    </div>
  )
}

// 移动端品类展示
const CategoryMobileItem = ({ data, configuration }: { data: CategoryItem[]; configuration?: any }) => {
  const totalCategories = configuration?.totalCategories ?? 0
  // 当数量少于容器时，第一个项目会占据更大空间
  const isFirstItemLarger = totalCategories <= MOBILE_FULL_PAGE_CAPACITY
  return (
    <div className={`gap-3 ${isFirstItemLarger ? 'grid grid-cols-2' : 'flex h-full flex-col'}`}>
      {data?.map((item, index) => (
        <SingleMobileItem
          key={index}
          index={index}
          data={item}
          configuration={configuration}
          isFirstItemLarger={isFirstItemLarger}
        />
      ))}
    </div>
  )
}

const Category = React.forwardRef<HTMLDivElement, CategoryProps>((props, ref) => {
  const { data, className = '', key, event, ...rest } = props
  // soundcore品牌品类不超过5个，单独做样式处理
  const currentData = (data?.isShowSelect ? data?.products : data?.productData) || []
  const isCardShow = currentData.length > MOBILE_FULL_PAGE_CAPACITY
  const totalCategories = currentData.length
  const mobileSlides = chunkArray(currentData, isCardShow ? MOBILE_COMPACT_PAGE_SIZE : MOBILE_FULL_PAGE_CAPACITY)

  const innerRef = useRef<HTMLDivElement>(null)

  useImperativeHandle(ref, () => innerRef.current as HTMLDivElement)

  return (
    <div
      {...rest}
      ref={innerRef}
      className={cn('w-full overflow-hidden', className, { 'aiui-dark': data?.theme === 'dark' })}
    >
      {data?.title && <Title data={{ title: data?.title }} className="text-4xl" />}
      <div className="tablet:block hidden">
        <SwiperBox
          id={`Category${key}`}
          Slide={CategoryPcItem}
          data={{
            list: currentData,
            configuration: { shape: data?.shape, event: event, title: data?.title, totalCategories },
          }}
          paginationClassName="[&_.swiper-pagination]:!-bottom-6"
        />
      </div>
      <div className="tablet:hidden block">
        <SwiperBox
          id={`Category1${key}`}
          Slide={CategoryMobileItem}
          data={{
            list: mobileSlides,
            configuration: { shape: data?.shape, event: event, title: data?.title, totalCategories },
          }}
          isHalf={isCardShow}
          paginationClassName="[&_.swiper-pagination]:!-bottom-6"
        />
      </div>
      <div className="h-7" />
    </div>
  )
})

Category.displayName = 'Category'

export default withLayout(Category)
